// ============================================================================
// LE CLAVIER D'UN GROUPE — tabulation glissante et flèches, pour les rôles qui le PROMETTENT.
//
// ⚠️ POURQUOI UNE PORTE. `role="radio"` annonce à une aide technique « bouton radio, 2 sur 3 » : elle attend qu'une seule
//    option soit dans l'ordre de tabulation (l'option choisie) et que les flèches déplacent le choix. Quinze groupes du
//    dépôt — le segmenté partagé compris — déclaraient le rôle sans rendre ni l'un ni l'autre : la tabulation les
//    traversait un par un, et les flèches annoncées ne faisaient rien. Écrit dans chaque écran, ce clavier divergerait ;
//    la garde `audit-roles-clavier` reconnaît cette porte à son nom (`{...roving…(…)}`).
// ⚠️ ON CIRCULE, ON NE BUTE PAS — comme les onglets (`tabAfterKey`) : au bout, la flèche revient au début.
// ⚠️ UNE OPTION DÉSACTIVÉE EST SAUTÉE : les flèches qui s'y poseraient laisseraient le focus sur un bouton inerte.
// ============================================================================
import type { KeyboardEvent } from 'react';

/**
 * Le pas d'une touche — `Origine`/`Fin` aux bouts.
 * ⚠️ UNE BARRE D'ONGLETS N'ÉCOUTE QUE L'AXE HORIZONTAL : y capter ↑/↓ volerait le défilement de la page à qui la quitte.
 */
function stepOf(key: string, axis: 'both' | 'horizontal'): 1 | -1 | 'first' | 'last' | null {
  if (key === 'ArrowRight' || (axis === 'both' && key === 'ArrowDown')) return 1;
  if (key === 'ArrowLeft' || (axis === 'both' && key === 'ArrowUp')) return -1;
  if (key === 'Home') return 'first';
  if (key === 'End') return 'last';
  return null;
}

/**
 * L'option que la touche désigne — ou `null` si elle ne concerne pas le groupe, ou si aucune option n'est atteignable.
 * Extraite pour être mesurable : ce paquet n'a pas d'environnement DOM.
 */
export function rovingTarget(
  key: string, index: number, count: number, disabled: (i: number) => boolean = () => false, axis: 'both' | 'horizontal' = 'both',
): number | null {
  const step = stepOf(key, axis);
  if (step === null || count === 0) return null;
  const from = step === 'first' ? -1 : step === 'last' ? count : index;
  const dir = step === 'first' ? 1 : step === 'last' ? -1 : step;
  for (let k = 1; k <= count; k += 1) {
    const i = (((from + dir * k) % count) + count) % count;
    if (!disabled(i)) return i;
  }
  return null;
}

/** Ce qu'une option d'un groupe exclusif doit savoir d'elle-même et de ses voisines. */
export interface RovingRadioOptions {
  index: number;
  count: number;
  /** L'option est-elle celle qui est choisie ? */
  checked: boolean;
  /** Une option du groupe est-elle choisie ? Sans choix, la PREMIÈRE entre dans la tabulation. */
  anyChecked: boolean;
  /** Choisit l'option désignée par les flèches — les flèches d'un groupe radio CHOISISSENT, elles ne font pas que viser. */
  onPick: (index: number) => void;
  disabled?: (index: number) => boolean;
}

/**
 * Les propriétés clavier d'une option de `role="radio"`, à étaler sur son élément.
 * ⚠️ LE FOCUS SUIT LE CHOIX : on cherche la voisine dans le `radiogroup` le plus proche, au rendu courant.
 */
export function rovingRadio({ index, count, checked, anyChecked, onPick, disabled }: RovingRadioOptions) {
  return {
    tabIndex: checked || (!anyChecked && index === 0) ? 0 : -1,
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      const target = rovingTarget(e.key, index, count, disabled);
      if (target === null) return;
      e.preventDefault();
      if (target !== index) onPick(target);
      const group = e.currentTarget.closest('[role="radiogroup"]');
      group?.querySelectorAll<HTMLElement>('[role="radio"]')[target]?.focus();
    },
  };
}

/**
 * Les propriétés clavier d'un onglet (`role="tab"`) écrit à la main, à étaler sur son élément — le composant `Tabs`
 * porte déjà les siennes.
 * ⚠️ L'ONGLET CHOISI SEUL EST DANS LA TABULATION, et ←/→ choisissent en déplaçant le focus (activation automatique) ;
 *    un onglet désactivé est sauté.
 */
export function rovingTab({ index, count, selected, onPick, disabled }: {
  index: number; count: number; selected: boolean; onPick: (index: number) => void; disabled?: (index: number) => boolean;
}) {
  return {
    tabIndex: selected ? 0 : -1,
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      const target = rovingTarget(e.key, index, count, disabled, 'horizontal');
      if (target === null) return;
      e.preventDefault();
      if (target !== index) onPick(target);
      const list = e.currentTarget.closest('[role="tablist"]');
      list?.querySelectorAll<HTMLElement>('[role="tab"]')[target]?.focus();
    },
  };
}

/**
 * L'entrée au clavier dans un menu dont le PANNEAU a le focus (un `Popover` en `role="menu"` le pose sur lui à
 * l'ouverture) : ↓ et `Origine` vont au premier élément, ↑ et `Fin` au dernier. À poser sur le panneau.
 * ⚠️ IL N'AGIT QUE SI LE FOCUS EST SUR LE PANNEAU LUI-MÊME : depuis un élément, la touche remonte jusqu'ici, et ce sont
 *    alors les flèches de l'élément (`rovingMenuItem`) qui décident.
 */
export function rovingMenuEntry(e: KeyboardEvent<Element>): void {
  if (e.target !== e.currentTarget) return;
  const first = e.key === 'ArrowDown' || e.key === 'Home';
  const last = e.key === 'ArrowUp' || e.key === 'End';
  if (!first && !last) return;
  const items = e.currentTarget.querySelectorAll<HTMLElement>('[role^="menuitem"]');
  if (items.length === 0) return;
  e.preventDefault();
  items[first ? 0 : items.length - 1]?.focus();
}

/**
 * Les propriétés clavier d'un élément de menu (`role="menuitem"` et ses variantes), à étaler sur son élément.
 * ⚠️ DANS UN MENU, LES FLÈCHES DÉPLACENT LE FOCUS SANS RIEN CHOISIR : c'est Entrée (le clic du bouton) qui agit. Aucun
 *    élément n'est dans l'ordre de tabulation — le menu reçoit le focus à son ouverture, sur son premier élément.
 */
export function rovingMenuItem({ index, count, disabled }: { index: number; count: number; disabled?: (index: number) => boolean }) {
  return {
    tabIndex: -1,
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      const target = rovingTarget(e.key, index, count, disabled);
      if (target === null) return;
      e.preventDefault();
      const menu = e.currentTarget.closest('[role="menu"]');
      menu?.querySelectorAll<HTMLElement>('[role^="menuitem"]')[target]?.focus();
    },
  };
}
